import { twMerge } from "tailwind-merge";
import { SupplierRelationshipGetResponseType } from "@sellar-io/api-v2/types";
import { getBusinessEntityTypeName } from "@sellar-io/common";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  DropdownLegacy,
  Icon,
  ProfilePicture,
  DropdownLegacyElement,
} from "../../../components";
import { BadPayerWarning } from "../../../components/badPayerWarning/BadPayerWarning";
import { RequestedCreditTermsAlert } from "../../../components/connectionPaymentTermsRequest/RequestedCreditTermsAlert";
import { EditDeliveryInstructionsDialog } from "../../../components/deliveryInstructions/EditDeliveryInstructionsDialog";
import { useUpdateConnectionDeliveryInstructions } from "../../../components/deliveryInstructions/useUpdateDeliveryInstructions";
import {
  InlineTab,
  InlineTabList,
  InlineTabGroup,
  InlineTabPanels,
  InlineTabPanel,
} from "../../../components/inlineTab/InlineTab";
import { OpeningOrderFeeLabel } from "../../../components/openingOrderFee/OpeningOrderFeeLabel";
import { ORDER_SUPPLIER } from "../../../constants/routes";
import { STOREFRONT_LINKS } from "../../../constants/routes";
import { useStorefrontLinksV2Store } from "../../storefrontLinks/v2/StorefrontLinksV2Store";
import { CustomerOrderList } from "../CustomerOrderList";
import { BusinessConnectionAnalytics } from "./BusinessConnectionAnalytics";
import { ConnectionIntegrationMappingCard } from "./ConnectionIntegrationMappingCard";
import { ConnectionAcceptModal } from "./connectionSelector/ConnectionAcceptModal";
import { ConnectionDeclineModal } from "./connectionSelector/ConnectionDeclineModal";
import { DeleteConnectionAsSupplierDialog } from "./connectionSelector/DeleteConnectionAsSupplierDialog";
import { useOpeningOrderFeeGetByBuyer } from "../../../hooks/api/v2/openingOrderFee/useOpeningOrderFeeGetByBuyer";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { BusinessConnectionConvertDiscountsCard } from "./BusinessConnectionConvertDiscountsCard";
import { BusinessConnectionTradeTerms } from "./BusinessConnectionTradeTerms";
import { BusinessConnectionPeople } from "./BusinessConnectionPeople";
import { BusinessConnectionTradePills } from "./BusinessConnectionTradePills";
import { AddressFragment } from "../../../components/addressFragment/AddressFragment";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DataButton,
  H1,
  H3,
  H5,
  H6,
} from "@sellar-io/components";
import { BusinessConnectionCustomerCode } from "./BusinessConnectionCustomerCode";

export function BusinessConnectionV2({
  connection,
}: {
  connection: SupplierRelationshipGetResponseType;
}) {
  const navigate = useNavigate();
  const [assignTermsActive, setAssignTermsActive] = useState<boolean>(false);

  const [deleteConnectionDialogActive, setDeleteConnectionDialogActive] =
    useState(false);

  const [editDeliveryInstructionsActive, setEditDeliveryInstructionsActive] =
    useState<boolean>(false);
  const [declineConnectionModalActive, setDeclineConnectionModal] =
    useState<boolean>(false);
  const {
    updateConnectionDeliveryInstructions,
    isLoading: updatingConnectionDeliveryInstructions,
  } = useUpdateConnectionDeliveryInstructions(connection.id);

  const remoteFeatureFlags = useRemoteFeatureFlags();
  const unbundlingRelease2bEnabled = remoteFeatureFlags.isActive(
    "unbundlingRelease2B",
  );

  const { setCustomer: setStorefrontLinksCustomer } =
    useStorefrontLinksV2Store();
  const retailerBusinessDeliveryInstructions =
    connection.Retailer.Business.deliveryInstructions;
  const connectionDeliveryInstructions = connection.deliveryInstructions;
  const deliveryInstructions =
    retailerBusinessDeliveryInstructions ||
    connectionDeliveryInstructions ||
    "No delivery instructions";

  return (
    <>
      <div className={"flex w-screen max-w-full flex-col gap-6 p-6"}>
        <div className={"z-10 flex flex-col gap-4"}>
          <div className="@container flex flex-wrap items-start gap-4">
            {connection.SupplierRetailerConnectionPaymentTermsRequest
              ?.status === "pending" && (
              <RequestedCreditTermsAlert
                requestedPaymentTerms={
                  connection.SupplierRetailerConnectionPaymentTermsRequest
                }
                buyerName={connection.Retailer.Business.name}
                connection={{
                  ...connection,
                  PaymentTermsRequest:
                    connection.SupplierRetailerConnectionPaymentTermsRequest,
                }}
              />
            )}
            <BadPayerWarning
              flaggedAsBadPayer={connection.Retailer.flaggedAsBadPayer}
            />
            <div className="w-full @3xl:w-auto">
              <div className="mb-4 flex items-center">
                <ProfilePicture
                  size={"large"}
                  logoUrl={connection.Retailer.Business.logoUrl}
                  name={connection.Retailer.Business.name}
                />

                <div className="ml-2">
                  <div className="flex items-center">
                    <H1 className="text-lg">
                      {connection.Retailer.Business.name}
                    </H1>
                    <BuyerOpeningOrderFeeLabel
                      buyerId={connection.BuyerId ?? 0}
                    />
                  </div>
                  <H6>
                    {[
                      connection.Retailer.Business.DeliveryAddress.city,
                      connection.Retailer.Business.DeliveryAddress.region,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </H6>
                </div>
              </div>
            </div>
            {connection.approved ? (
              <div
                className={
                  "mt-4 flex w-full flex-row flex-wrap gap-3 @3xl:mt-0 @3xl:ml-auto @3xl:w-auto"
                }
              >
                <Button
                  className="order-2 @3xl:order-1"
                  color={"secondary"}
                  onClick={() => {
                    setStorefrontLinksCustomer({
                      connectionId: connection.id,
                      name: connection.Retailer.Business.name,
                      logoUrl: connection.Retailer.Business.logoUrl ?? "",
                      postalCode:
                        connection.Retailer.Business.DeliveryAddress.postalCode,
                      priceTierName: connection.PriceTier?.name ?? "",
                      availabilityTierName:
                        connection.AvailabilityTier?.name ?? "",
                    });
                    return navigate(STOREFRONT_LINKS);
                  }}
                >
                  Share link
                </Button>
                <Button
                  className="order-1 @3xl:order-2"
                  onClick={() => {
                    navigate(
                      ORDER_SUPPLIER.href({ connectionId: connection.id }),
                    );
                  }}
                >
                  Place order
                </Button>

                <div className={"order-4 flex items-center gap-2"}>
                  <DropdownLegacy>
                    {!connection.approved && (
                      <DropdownLegacyElement
                        onClick={() => setDeclineConnectionModal(true)}
                      >
                        Decline connection
                      </DropdownLegacyElement>
                    )}
                    {connection.approved && (
                      <DropdownLegacyElement
                        onClick={() => setDeleteConnectionDialogActive(true)}
                        className="text-red-500"
                      >
                        Delete customer
                      </DropdownLegacyElement>
                    )}
                  </DropdownLegacy>
                </div>
              </div>
            ) : (
              <div className={"flex items-center gap-2"}>
                <Button
                  className={"col-span-1"}
                  color={"primary"}
                  onClick={() => setAssignTermsActive(true)}
                >
                  <Icon icon={"HANDSHAKE"} />
                  Assign terms
                </Button>
                <DropdownLegacy>
                  <DropdownLegacyElement
                    onClick={() => setDeclineConnectionModal(true)}
                  >
                    Decline connection
                  </DropdownLegacyElement>
                </DropdownLegacy>
              </div>
            )}
          </div>
        </div>

        <BusinessConnectionTradePills connection={connection} />

        <BusinessConnectionAnalytics connectionId={connection.id} />

        <InlineTabGroup>
          <div className={"mb-6 flex flex-wrap justify-between gap-2"}>
            <InlineTabList>
              <InlineTab>Configure customer</InlineTab>
              {connection.approved && (
                <>
                  <InlineTab>Business info</InlineTab>
                  <InlineTab>Orders</InlineTab>
                </>
              )}
            </InlineTabList>
          </div>
          <InlineTabPanels>
            <InlineTabPanel className={"flex flex-col gap-6"}>
              {unbundlingRelease2bEnabled && (
                <BusinessConnectionConvertDiscountsCard
                  connectionId={connection.id}
                />
              )}
              <BusinessConnectionTradeTerms connection={connection} />
              <BusinessConnectionPeople
                RetailerId={connection.BuyerId ?? 0}
                buyerName={connection.Retailer.Business.name}
                connectionType={connection.connectionType}
              />
              <BusinessConnectionCustomerCode connection={connection} />
              <ConnectionIntegrationMappingCard connection={connection} />
            </InlineTabPanel>
            <InlineTabPanel className={"flex flex-col gap-6"}>
              <Card className="bg-white">
                <CardHeader
                  className={twMerge(
                    "bg-white",
                    "flex items-center justify-between",
                  )}
                >
                  <H3>Addresses</H3>
                </CardHeader>
                <CardContent className={"grid gap-y-4 md:grid-cols-2"}>
                  <div className={"flex flex-col"}>
                    <H5>Delivery address</H5>
                    <AddressFragment
                      address={connection.Retailer.Business.DeliveryAddress}
                    />
                  </div>
                  <div className={"flex flex-col"}>
                    <H5>Invoice address</H5>
                    <AddressFragment
                      address={connection.Retailer.Business.InvoiceAddress}
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardHeader
                  className={twMerge(
                    "bg-white",
                    "flex items-center justify-between",
                  )}
                >
                  <div>
                    <H3>Delivery instructions</H3>
                    <H6>
                      For your reference only, include any useful information to
                      remember when delivering to this customer
                    </H6>
                  </div>
                  {!retailerBusinessDeliveryInstructions?.length && (
                    <DataButton
                      border={true}
                      onClick={() => setEditDeliveryInstructionsActive(true)}
                    >
                      <Icon icon={"PENCIL"} size={0.7} />
                      Edit
                    </DataButton>
                  )}
                </CardHeader>
                <CardContent className={"grid gap-y-4 md:grid-cols-2"}>
                  <p>{deliveryInstructions}</p>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardHeader
                  className={twMerge(
                    "bg-white",
                    "flex items-center justify-between",
                  )}
                >
                  <H3>Additional contact info</H3>
                </CardHeader>
                <CardContent className={"grid gap-y-4 md:grid-cols-2"}>
                  {connection.connectionType === "customer" && (
                    <>
                      <div>
                        <H5>Phone number</H5>
                        <span>
                          {connection.Retailer.Business.phoneNumber || "N/A"}
                        </span>
                      </div>
                    </>
                  )}
                  <div>
                    <H5>Website</H5>
                    <span>
                      {connection.Retailer.Business.websiteUrl || "N/A"}
                    </span>
                  </div>
                  <div>
                    <H5>Instagram</H5>
                    <span>
                      {connection.Retailer.Business.instagram || "N/A"}
                    </span>
                  </div>
                  <div>
                    <H5>Twitter</H5>
                    <span>{connection.Retailer.Business.twitter || "N/A"}</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardHeader
                  className={twMerge(
                    "bg-white",
                    "flex items-center justify-between",
                  )}
                >
                  <H3>Company info</H3>
                </CardHeader>
                <CardContent className={"grid gap-y-4 md:grid-cols-2"}>
                  <div>
                    <H5>Trading name</H5>
                    <span>{connection.Retailer.Business.name}</span>
                  </div>
                  <div>
                    <H5>VAT number</H5>
                    <span>
                      {connection.Retailer.Business.vatNumber ?? "N/A"}
                    </span>
                  </div>
                  <div>
                    <H5>Company name</H5>
                    <span>
                      {connection.Retailer.Business.registeredCompanyName ??
                        "N/A"}
                    </span>
                  </div>
                  <div>
                    <H5>Company number</H5>
                    <span>
                      {connection.Retailer.Business.companyNumber ?? "N/A"}
                    </span>
                  </div>
                  <div>
                    <H5>Business entity type</H5>
                    <span>
                      {getBusinessEntityTypeName(
                        connection.Retailer.Business.entityType ?? undefined,
                      )}
                    </span>
                  </div>
                  <div>
                    <H5>AWRS number</H5>
                    <span>
                      {connection.Retailer.Business.awrsNumber ?? "N/A"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </InlineTabPanel>
            <InlineTabPanel className={"flex w-full flex-col gap-6"}>
              <BuyerOpeningOrderFeeLabel
                fullLabel={true}
                buyerId={connection.BuyerId ?? 0}
              />
              <CustomerOrderList connection={connection} />
            </InlineTabPanel>
          </InlineTabPanels>
        </InlineTabGroup>
      </div>
      <DeleteConnectionAsSupplierDialog
        active={deleteConnectionDialogActive}
        onDismiss={() => {
          setDeleteConnectionDialogActive(false);
        }}
        connection={connection}
      />
      <ConnectionDeclineModal
        active={declineConnectionModalActive}
        onDismiss={() => setDeclineConnectionModal(false)}
        connectionId={connection.id}
      />
      <ConnectionAcceptModal
        active={assignTermsActive}
        onDismiss={() => setAssignTermsActive(false)}
        connection={connection}
      />
      <EditDeliveryInstructionsDialog
        description="Include any useful information to remember when delivering to this customer"
        initialDeliveryInstructions={connection.deliveryInstructions ?? ""}
        active={editDeliveryInstructionsActive}
        onDismiss={() => setEditDeliveryInstructionsActive(false)}
        onSubmit={(data) => {
          updateConnectionDeliveryInstructions({
            data,
            onSuccess: () => {
              setEditDeliveryInstructionsActive(false);
            },
          });
        }}
        loading={updatingConnectionDeliveryInstructions}
      />
    </>
  );
}

const BuyerOpeningOrderFeeLabel = ({
  fullLabel = false,
  buyerId,
}: {
  fullLabel?: boolean;
  buyerId: number;
}) => {
  const openingOrderFee = useOpeningOrderFeeGetByBuyer(buyerId);

  if (!openingOrderFee.data) return;

  const fee = openingOrderFee.data;

  if (!fee.Order) return;

  return (
    <div className="mt-3">
      <OpeningOrderFeeLabel fee={fee} fullLabel={fullLabel} />
    </div>
  );
};
